/**
 * Unify DocumentReference model — comprehensive FHIR schema.
 *
 * DocumentReferences track uploaded documents, admin notes, discharge summaries,
 * clinical documents, and progress note addendums.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ Field              │ Type                        │ Description                │
 * ├──────────────────────────────────────────────────────────────────────────────┤
 * │ status             │ code                        │ current|superseded|entered  │
 * │ docStatus          │ code                        │ preliminary|final|amended   │
 * │ type               │ CodeableConcept             │ DOCUMENT_TYPE_SYSTEM coding │
 * │ category[]         │ CodeableConcept[]           │ DOCUMENT_CATEGORY_SYSTEM    │
 * │ subject            │ Reference<Patient>          │ Patient this doc is about   │
 * │ author[]           │ Reference<Practitioner>[]   │ Who authored the document   │
 * │ date               │ instant                     │ When reference was created  │
 * │ description        │ string                      │ Document description        │
 * │ content[]          │ Content[]                   │ Attachment(s) with data/url │
 * │ context.encounter  │ Reference<Encounter>[]      │ Encounter context           │
 * │ context.related    │ Reference[]                 │ EoC, QR, or other context   │
 * │ relatesTo[]        │ RelatesTo[]                 │ Parent doc (for addendums)  │
 * │ securityLabel[]    │ CodeableConcept[]           │ Confidentiality codes       │
 * │ meta.tag[]         │ Coding[]                    │ APPOINTMENT_TAG_SYSTEM      │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * type coding system: DOCUMENT_TYPE_SYSTEM
 *   Codes: admin-note, discharge-summary, clinical-document, progress-note-addendum,
 *          document, fax, psychotherapy-note
 *
 * category coding system: DOCUMENT_CATEGORY_SYSTEM
 *   Codes: admin, clinical, addendum
 *
 * meta.tag systems:
 *  - APPOINTMENT_TAG_SYSTEM → links addendum / psychotherapy DocumentReference to an Appointment
 *
 * Psychotherapy notes (HIPAA process notes):
 *  - type.coding[0].code = "psychotherapy-note"
 *  - category[0].coding[0].code = "clinical"
 *  - docStatus = "preliminary" (never locked; always editable by author)
 *  - securityLabel = v3-Confidentiality | R
 *  - content[0].attachment = text/plain (base64 body)
 *  - author = writing Practitioner; one note per (appointment, author)
 *  - Do NOT stamp CareTeam meta.accounts (author-scoped AccessPolicy only)
 *
 * Addendum-specific fields:
 *  - type.coding[0].code = "progress-note-addendum"
 *  - category[0].coding[0].code = "addendum"
 *  - content[0].attachment.data = base64 JSON payload with:
 *      { sequenceNumber, reasonForChange, authorName, authorCredentials,
 *        originalNoteDate, lineChanges[] }
 *  - context.related[0] → QuestionnaireResponse (the note being amended)
 *  - context.encounter[0] → Encounter
 *  - meta.tag with APPOINTMENT_TAG_SYSTEM → appointment id
 */

import type { DocumentReference, Reference, Encounter, EpisodeOfCare, Patient, Practitioner } from '@medplum/fhirtypes';

import {
  DOCUMENT_TYPE_SYSTEM,
  DOCUMENT_CATEGORY_SYSTEM,
  APPOINTMENT_TAG_SYSTEM,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
//  Coded value types
// ═══════════════════════════════════════════════════════════════════════════

/** DocumentReference.status values used by Unify. */
export type DocumentReferenceStatus = 'current' | 'superseded' | 'entered-in-error';

/** DocumentReference.docStatus values. */
export type DocumentDocStatus = 'preliminary' | 'final' | 'amended' | 'entered-in-error';

/** Document type codes (DOCUMENT_TYPE_SYSTEM). */
export type DocumentTypeCode =
  | 'admin-note'
  | 'discharge-summary'
  | 'clinical-document'
  | 'progress-note-addendum'
  | 'document'
  | 'fax'
  | 'psychotherapy-note'
  | 'historical-progress-note'
  | 'historical-psychotherapy-note'
  | 'historical-intake-note'
  | 'historical-mental-status-exam'
  | 'historical-chart-note'
  | 'historical-assessment-note'
  | 'historical-treatment-plan'
  | 'historical-form'
  | 'historical-document'
  | 'stedi-837p-json'
  | 'vob-raw-json';

/** Document category codes (DOCUMENT_CATEGORY_SYSTEM). */
export type DocumentCategoryCode =
  | 'admin'
  | 'clinical'
  | 'addendum';

/** Reason for change in a progress note addendum. */
export type AddendumReasonForChange = 'typo' | 'clarification' | 'other';

/** Shape of the addendum payload stored as base64 JSON in content[0].attachment.data. */
export interface AddendumPayload {
  sequenceNumber: number;
  reasonForChange: AddendumReasonForChange;
  authorName: string;
  authorCredentials?: string;
  originalNoteDate: string;
  lineChanges?: Array<{ removed?: string; added?: string }>;
  originalLines?: string[];
  afterLines?: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Get the document type code from type.coding (DOCUMENT_TYPE_SYSTEM). */
export function getDocumentType(resource: DocumentReference): DocumentTypeCode | undefined {
  return resource.type?.coding?.find((c) => c.system === DOCUMENT_TYPE_SYSTEM)?.code as DocumentTypeCode | undefined;
}

/** Get the document category code from category[].coding (DOCUMENT_CATEGORY_SYSTEM). */
export function getDocumentCategory(resource: DocumentReference): DocumentCategoryCode | undefined {
  return resource.category
    ?.flatMap((cc) => cc.coding ?? [])
    .find((c) => c.system === DOCUMENT_CATEGORY_SYSTEM)?.code as DocumentCategoryCode | undefined;
}

/** Get the patient subject reference. */
export function getDocRefSubjectRef(resource: DocumentReference): Reference | undefined {
  return resource.subject;
}

/** Get the first author reference. */
export function getAuthorRef(resource: DocumentReference): Reference | undefined {
  return resource.author?.[0];
}

/** Get the linked appointment id from meta.tag (for addendums). */
export function getDocRefAppointmentId(resource: DocumentReference): string | undefined {
  return resource.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code;
}

/** Get the encounter reference from context.encounter[0]. */
export function getEncounterRef(resource: DocumentReference): Reference | undefined {
  return resource.context?.encounter?.[0];
}

/** Get the first context.related reference (EpisodeOfCare or QuestionnaireResponse). */
export function getContextRelatedRef(resource: DocumentReference): Reference | undefined {
  return resource.context?.related?.[0];
}

/** Check if this is a progress note addendum. */
export function isAddendum(resource: DocumentReference): boolean {
  return getDocumentType(resource) === 'progress-note-addendum';
}

/** Check if this is a HIPAA-style private psychotherapy note (native or historical type code). */
export function isPsychotherapyNote(resource: DocumentReference): boolean {
  const code = getDocumentType(resource);
  return code === 'psychotherapy-note' || code === 'historical-psychotherapy-note';
}

/** Decode plain-text psychotherapy note body from content[0].attachment.data. */
export function getPsychotherapyNoteText(resource: DocumentReference): string {
  const attachment = resource.content?.[0]?.attachment;
  if (!attachment) return '';
  // Empty string data is valid (empty note). Do not fall back to attachment.title —
  // that is a fixed label ("Psychotherapy note"), not body text.
  if (attachment.data != null) {
    try {
      return typeof atob === 'function'
        ? atob(attachment.data)
        : Buffer.from(attachment.data, 'base64').toString('utf-8');
    } catch {
      return '';
    }
  }
  return '';
}

/** Encode and set psychotherapy note plain text in content[0].attachment.data. */
export function setPsychotherapyNoteText(resource: DocumentReference, text: string): void {
  const b64 =
    typeof btoa === 'function' ? btoa(text) : Buffer.from(text, 'utf-8').toString('base64');
  resource.content = [
    {
      attachment: {
        contentType: 'text/plain; charset=utf-8',
        data: b64,
        title: 'Psychotherapy note',
      },
    },
  ];
}

/** Decode the addendum payload from content[0].attachment.data (base64 JSON). */
export function getAddendumPayload(resource: DocumentReference): AddendumPayload | undefined {
  const b64 = resource.content?.[0]?.attachment?.data;
  if (!b64) return undefined;
  try {
    const json = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf-8');
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  Setter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Set the document type coding. */
export function setDocumentType(resource: DocumentReference, code: DocumentTypeCode, display?: string): void {
  resource.type = { coding: [{ system: DOCUMENT_TYPE_SYSTEM, code, display }] };
}

/** Set the document category coding. */
export function setDocumentCategory(resource: DocumentReference, code: DocumentCategoryCode, display?: string): void {
  resource.category = [{ coding: [{ system: DOCUMENT_CATEGORY_SYSTEM, code, display }] }];
}

/** Set the patient subject reference. */
export function setDocRefSubjectRef(resource: DocumentReference, ref: Reference<Patient>): void {
  resource.subject = ref;
}

/** Set the author reference(s). */
export function setAuthorRef(resource: DocumentReference, ref: Reference<Practitioner>): void {
  resource.author = [ref];
}

/** Set the linked appointment id in meta.tag (for addendums). */
export function setDocRefAppointmentId(resource: DocumentReference, appointmentId: string): void {
  if (!resource.meta) resource.meta = {};
  if (!resource.meta.tag) resource.meta.tag = [];
  const existing = resource.meta.tag.find((t) => t.system === APPOINTMENT_TAG_SYSTEM);
  if (existing) {
    existing.code = appointmentId;
  } else {
    resource.meta.tag.push({ system: APPOINTMENT_TAG_SYSTEM, code: appointmentId });
  }
}

/** Set the encounter context. */
export function setEncounterRef(resource: DocumentReference, ref: Reference<Encounter>): void {
  if (!resource.context) resource.context = {};
  resource.context.encounter = [ref];
}

/** Set the context.related reference (EpisodeOfCare or QuestionnaireResponse). */
export function setContextRelatedRef(resource: DocumentReference, ref: Reference<EpisodeOfCare>): void {
  if (!resource.context) resource.context = {};
  resource.context.related = [ref];
}

/** Encode and set the addendum payload as base64 JSON in content[0].attachment.data. */
export function setAddendumPayload(resource: DocumentReference, payload: AddendumPayload): void {
  const json = JSON.stringify(payload);
  const b64 = typeof btoa === 'function' ? btoa(json) : Buffer.from(json, 'utf-8').toString('base64');
  resource.content = [{ attachment: { contentType: 'application/json', data: b64 } }];
}

const CONFIDENTIALITY_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality';

export interface BuildPsychotherapyNoteInput {
  patientRef: Reference<Patient>;
  authorRef: Reference<Practitioner>;
  appointmentId: string;
  text?: string;
  encounterRef?: Reference<Encounter>;
}

/**
 * Build a new author-scoped psychotherapy note DocumentReference.
 * Caller must create/update via Medplum; do not stamp CareTeam compartment.
 */
export function buildPsychotherapyNote(input: BuildPsychotherapyNoteInput): DocumentReference {
  const doc: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'preliminary',
    date: new Date().toISOString(),
    securityLabel: [
      {
        coding: [
          {
            system: CONFIDENTIALITY_SYSTEM,
            code: 'R',
            display: 'Restricted',
          },
        ],
      },
    ],
    content: [],
  };
  setDocumentType(doc, 'psychotherapy-note', 'Psychotherapy note');
  setDocumentCategory(doc, 'clinical', 'Clinical');
  setDocRefSubjectRef(doc, input.patientRef);
  setAuthorRef(doc, input.authorRef);
  setDocRefAppointmentId(doc, input.appointmentId);
  if (input.encounterRef) {
    setEncounterRef(doc, input.encounterRef);
  }
  setPsychotherapyNoteText(doc, input.text ?? '');
  return doc;
}
